import { forwardRef, useCallback, type CSSProperties, type ReactNode } from "react";
import { Flag } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { hideCursorTip } from "@/components/shared/CursorTip";
import { borderColorForBg, boostFlagSaturation, flagColorFor, saturateForDark, textColorForBg } from "@/lib/constants";
import { cn } from "@/lib/utils";
import type { DropdownOption } from "@/types/backlog";

/**
 * The backlog table's own property controls, as components.
 *
 * The table long ago stopped drawing priority, status and type as comboboxes:
 * the DISPLAY is the control. A flag opens the priority list, the filled
 * status pill opens the status list, the type chip opens the type list, and
 * nothing reboxes when a picker opens because the trigger is the same element
 * either way.
 *
 * They live here because the new-ticket form now uses exactly the same three —
 * "dropdowns for everything is bleh", and a form whose controls are the ones
 * the table taught you needs no second vocabulary. Extracted rather than
 * copied, so the two can't drift.
 */

/**
 * The ticket page's edit-in-place affordance: a secondary wash plus a soft ring
 * on hover, so everything editable says so before you commit a click. Shared by
 * the property strip and the custom Fields group under it (#9399).
 */
export const INLINE_EDIT_HOVER =
  "cursor-pointer rounded-md -mx-2 -my-1 px-2 py-1 transition-colors hover:bg-secondary hover:ring-1 hover:ring-ring/40";

/** The small uppercase label above each field on the ticket page. */
export const PROPERTY_LABEL = "block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground";

/** The wash every one of these triggers takes on hover and while open. */
export const PROPERTY_TRIGGER_WASH =
  "transition-colors hover:bg-[hsl(var(--ring)/0.18)] data-[state=open]:bg-[hsl(var(--ring)/0.18)]";

interface PriorityFlagProps {
  priority: DropdownOption | null | undefined;
  className?: string;
}

/**
 * The priority flag.
 *
 * The dropdown palette is pastel (built for filled pills), so the flag is that
 * pastel filled and outlined by a darker step of itself (`borderColorForBg`),
 * which is what makes an 18px glyph carry. `flagColorFor` keeps a saturated
 * stored colour — Critical's #b10202 — from shouting a register louder than
 * its rank, and takes the red end of the ladder a tenth further out of the
 * white so High and Medium stop reading as one rank at 18px.
 *
 * `boostFlagSaturation` then applies a later, per-rank saturation bump (High
 * more than Medium/Low, Critical untouched) to both the fill and the outline
 * at their own point of computation, so it lands wherever either has
 * headroom rather than only where the fill does.
 *
 * Dark mode is the SAME two colours with a tenth more saturation each
 * (`saturateForDark`), no more — computed from the already-boosted fill and
 * outline, so the bump reaches dark mode without a separate step. Both pairs
 * are published as custom properties and `.flag-glyph` (index.css) picks
 * between them, because this style is inline and an inline style cannot
 * answer `.dark`.
 */
export const PriorityFlag = ({ priority, className }: PriorityFlagProps) => {
  if (!priority) return <span className="text-muted-foreground">-</span>;
  const fill = boostFlagSaturation(flagColorFor(priority.color), priority.id);
  const outline = boostFlagSaturation(borderColorForBg(flagColorFor(priority.color)), priority.id);
  return (
    <Flag
      className={cn("flag-glyph h-[18px] w-[18px] transition-transform hover:scale-110", className)}
      style={
        {
          "--flag-fill": fill,
          "--flag-outline": outline,
          "--flag-fill-dark": saturateForDark(fill),
          "--flag-outline-dark": saturateForDark(outline),
        } as CSSProperties
      }
      aria-hidden
    />
  );
};

interface StatusPillProps {
  status: DropdownOption | null | undefined;
  className?: string;
}

/**
 * The filled status pill — monday-style, because status is the column you scan
 * and a block of colour is read before any word in it. `textColorForBg` picks
 * the readable ink for the stored colour, so the pastel palette stays legible
 * in both themes.
 */
export const StatusPill = ({ status, className }: StatusPillProps) => {
  if (!status) return <span className="text-muted-foreground">-</span>;
  return (
    <span
      className={cn(
        "status-pill flex items-center justify-center truncate rounded-md px-2.5 py-1.5 text-xs font-semibold",
        className,
      )}
      style={{ backgroundColor: status.color, color: textColorForBg(status.color, status.id) }}
    >
      {status.label}
    </span>
  );
};

interface TypeChipProps {
  type: DropdownOption | null | undefined;
  className?: string;
}

/** The ticket-type chip: the stored colour at 20%, inked and outlined in it. */
export const TypeChip = ({ type, className }: TypeChipProps) => {
  if (!type) return <span className="text-muted-foreground">-</span>;
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center whitespace-nowrap rounded border px-2 py-0.5 text-[11px] font-medium",
        className,
      )}
      style={{
        backgroundColor: `${type.color}20`,
        color: type.color,
        borderColor: borderColorForBg(`${type.color}20`),
      }}
    >
      {type.label}
    </span>
  );
};

interface OptionPickerProps {
  options: DropdownOption[];
  onSelect: (option: DropdownOption) => void;
  /** The option currently stored on the ticket, marked as such in the list. */
  selectedId?: string | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: "start" | "center" | "end";
  side?: "top" | "bottom";
  className?: string;
  /** The trigger — one of the three displays above, wrapped in a button. */
  children: ReactNode;
}

/**
 * A value no option can carry, handed to cmdk as its initial selection.
 *
 * cmdk insists on having exactly one item selected: on mount, as each item
 * registers, it runs "if nothing is selected, select the first one". That is
 * how these lists came to open with their top row pre-highlighted — a
 * highlight the reader did not ask for, sitting somewhere their pointer is
 * not, and which the first pointer move then appears to *leave*.
 *
 * The auto-select is guarded by a falsy check on the store's value
 * (`state.value || selectFirstItem()`), so a `defaultValue` that no item can
 * match satisfies it without selecting anything: the store opens non-empty,
 * the guard never fires, and `[aria-selected="true"]` matches no row. cmdk's
 * own lookups for "the selected item" are DOM queries for that attribute, so
 * every one of them correctly finds nothing until the reader acts:
 *
 * - **Pointer.** The first `pointermove` over a row selects it, normally.
 * - **Arrow keys.** "Next from nothing" lands on index 0 and, with `loop`,
 *   "previous from nothing" lands on the last row — so either key shows its
 *   highlight on the first press.
 * - **Enter while nothing is selected.** cmdk looks up the selected row, finds
 *   none, and dispatches nothing: the hidden first item cannot be picked by
 *   accident, which is the failure the old pre-highlight was one keystroke
 *   away from.
 * - **Reopening.** Radix unmounts the popover's contents on close, so the next
 *   open builds a fresh cmdk store that starts from this sentinel again.
 *
 * Controlling `value` instead does NOT work here: cmdk mutates its store
 * before consulting the controlled prop and only re-reads the prop when it
 * *changes*, so pushing the value back to "" — the value it already is — is a
 * no-op and the first row stays selected.
 */
const NO_OPTION_SELECTED = "__no-option-selected__";

/**
 * The list every one of the three hangs off: a coloured square and a label per
 * row. Centred under its trigger with its own width rather than sized to it —
 * the list is wider than a flag, and a collision padding keeps it clear of the
 * window edge instead of spilling past it.
 *
 * The row holding the CURRENT value takes the workspace switcher's treatment
 * (see TopNav): a faint standing box — a tint plus a matching hairline ring in
 * the menu's accent hue (`bg-accent/10`, `ring-accent/20`) — and a semibold
 * label. It is the same "this row is special" language cmdk's own highlight
 * already speaks on every row, turned down for a row that has to stand out at
 * rest rather than only on interaction.
 *
 * The tint is scoped to `:not([data-selected=true])` so the two never fight
 * over the same `background-color`: the moment cmdk highlights the row the
 * standing tint's selector stops matching and the highlight fill wins
 * outright, no specificity gamble. That fill is taken a step up on this row
 * only (`bg-focus/20`), because in light mode `--accent` and `--focus-border`
 * are the same colour and a 10% fill landing on a 10% tint would be no change
 * at all. The ring is a box-shadow — a different property — so it keeps
 * showing straight through the highlight: the row still reads as "the current
 * one" while highlighted.
 *
 * What it replaced: a 3px `--brand-ink` rail and a trailing Check. Both said
 * the same thing twice, in a vocabulary nothing else in the app's menus
 * speaks, and the rail's inset shadow is the property the ring now needs.
 *
 * `aria-selected` is cmdk's, for the roving highlight, so the stored value is
 * marked with `aria-current` — the switcher's attribute for the same idea.
 */
export const OptionPicker = ({
  options,
  onSelect,
  selectedId,
  open,
  onOpenChange,
  align = "center",
  side = "bottom",
  className,
  children,
}: OptionPickerProps) => {
  /**
   * Bring the current option into view on open, centred.
   *
   * Nothing is selected now, so cmdk's own scroll-the-selection-into-view has
   * nothing to scroll to, and a status list long enough to overflow would open
   * on its first row with the ticket's actual status somewhere below the fold.
   * A callback ref rather than an effect: the popover's contents are built
   * fresh on every open, so the ref firing IS the open, and it fires with the
   * rows already mounted and styled — measured and scrolled before the first
   * paint, so the list never visibly jumps.
   */
  const centreCurrentOption = useCallback(
    (list: HTMLDivElement | null) => {
      if (!list || !selectedId) return;
      const row = list.querySelector<HTMLElement>("[data-current]");
      if (!row) return;
      // `offsetTop` is measured from a shared offset parent (the popover), so
      // the list's own offset cancels out of the difference.
      const rowTop = row.offsetTop - list.offsetTop;
      list.scrollTop = Math.max(0, rowTop - list.clientHeight / 2 + row.offsetHeight / 2);
    },
    [selectedId],
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        // The list opens under the cursor that summoned it, so the trigger's
        // hover tip would sit on top of it until the pointer next moved.
        if (next) hideCursorTip();
        onOpenChange?.(next);
      }}
    >
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align={align}
        side={side}
        sideOffset={6}
        collisionPadding={12}
        className={cn("w-48 border-border bg-card p-0", className)}
      >
        {/* `tabIndex={0}`: cmdk listens for the arrow keys on its own root,
            which it renders with `tabIndex={-1}` — so Radix's focus scope,
            finding nothing tabbable inside the popover, focuses the popover
            itself and every keystroke fires on an ancestor of the listener.
            Making the root tabbable is what puts focus on it, and cmdk moves
            focus down to the list on the first keypress from there. */}
        <Command tabIndex={0} loop defaultValue={NO_OPTION_SELECTED} className="bg-card focus:outline-none">
          <CommandList ref={centreCurrentOption} className="max-h-72 bg-card">
            <CommandGroup>
              {options.map((option) => {
                const current = !!selectedId && option.id === selectedId;
                return (
                  <CommandItem
                    key={option.id}
                    value={option.label}
                    data-option-id={option.id}
                    data-current={current || undefined}
                    aria-current={current ? "true" : undefined}
                    className={cn(
                      "cursor-pointer text-foreground data-[selected=true]:bg-focus/10 data-[selected=true]:text-focus",
                      current &&
                        "ring-1 ring-inset ring-accent/20 [&:not([data-selected=true])]:bg-accent/10 data-[selected=true]:bg-focus/20",
                    )}
                    onSelect={() => onSelect(option)}
                  >
                    <div className="mr-2 h-3 w-3 shrink-0 rounded" style={{ backgroundColor: option.color }} />
                    <span className={cn(current && "font-semibold")}>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

interface PropertyTriggerProps {
  label: string;
  className?: string;
  children: ReactNode;
}

/**
 * A property control as a FORM field: the glyph with a small label above it.
 *
 * In a table the column header does this job, and the flag alone is legible
 * because it is the only thing in a column of flags. In a form there is no
 * column and no neighbours to compare against, so a bare flag is a puzzle —
 * the label is what makes the same control self-evident the first time.
 */
export const PropertyField = ({ label, className, children }: PropertyTriggerProps) => (
  <div className={cn("flex min-w-0 flex-col gap-1", className)}>
    {/* `whitespace-nowrap`: a label that wraps to two lines pushes its own
        control down and takes the strip's row of labels out of alignment —
        one field's label sitting a line above its neighbours' reads as a
        rendering fault. Each slot is sized to hold its label on one line. */}
    <span className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </span>
    {children}
  </div>
);

/** The button the three displays are wrapped in — one shape, one wash. */
export const PropertyTriggerButton = forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { className?: string }
>(({ className, children, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    {...props}
    className={cn(
      "flex h-9 items-center justify-center rounded-md border border-border bg-card px-2",
      PROPERTY_TRIGGER_WASH,
      className,
    )}
  >
    {children}
  </button>
));
PropertyTriggerButton.displayName = "PropertyTriggerButton";
